"use client"
import { ChampignonsCreateInput, ChampignonsListResponse, ChampignonsResponse, ChampignonsSelectInput, ChampignonsSelectOption, ChampignonsUpdateInput } from "@repo/types";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { services } from '../../../lib/api/services';
import { useQueryWithConfig } from '../useQueryWithConfig';

// Hooks for fetching champignons
export const useChampignons = (filters: ChampignonsSelectInput) => {
  const { page = 1, limit = 10, ...filterParams } = filters;
  
  // Handle search terms efficiently - prepare search params based on what's available
  const searchParams = Object.fromEntries(
    Object.entries(filterParams).filter(([_, value]) => value !== undefined && value !== null && value !== '')
  );
  
  const keys = [
    "champignons", 
    "list", 
    page, 
    limit,
    Object.keys(searchParams).length > 0 ? JSON.stringify(searchParams) : null
  ].filter(Boolean);
  
  return useQueryWithConfig<ChampignonsListResponse>(
    keys as string[],
    () => services.champignon.getChampignons({ 
      page, 
      limit,
      includeDeleted:false,
      ...searchParams 
    }),
    {
      // Avoid unnecessary refetches when search parameters haven't actually changed
      staleTime: 1000 * 60, // 1 minute
    }
  );
};

export const useChampignon = (id: number) => {
  return useQueryWithConfig<ChampignonsResponse>(
    ["champignons", "detail", id],
    () => services.champignon.getChampignonById(id),
    {
      enabled: !!id,
    }
  );
};

export const useChampignonOptions = () => {
  return useQueryWithConfig<ChampignonsSelectOption[]>(
    ["champignons", "options"],
    () => services.champignon.getChampignonOptions()
  );
};

export const useChampignonCategories = () => {
  return useQueryWithConfig<string[]>(
    ["champignons", "categories"],
    () => services.champignon.getChampignonCategories()
  );
};

// Hooks for champignon mutations
export const useCreateChampignon = () => {
  const queryClient = useQueryClient();
  
  return useMutation<ChampignonsResponse, Error, ChampignonsCreateInput>({
    mutationFn: (data) => services.champignon.createChampignon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["champignons", "list"] });
      queryClient.invalidateQueries({ queryKey: ["champignons", "options"] });
    },
  });
};

export const useUpdateChampignon = () => {
  const queryClient = useQueryClient();
  
  return useMutation<ChampignonsResponse, Error, { id: number; data: ChampignonsUpdateInput }>({
    mutationFn: ({ id, data }) => services.champignon.updateChampignon(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["champignons", "detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["champignons", "list"] });
      queryClient.invalidateQueries({ queryKey: ["champignons", "options"] });
    },
  });
};

export const useDeleteChampignon = () => {
  const queryClient = useQueryClient();
  
  return useMutation<ChampignonsResponse, Error, number>({
    mutationFn: (id) => services.champignon.deleteChampignon(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["champignons", "detail", id] });
      queryClient.invalidateQueries({ queryKey: ["champignons", "list"] });
      queryClient.invalidateQueries({ queryKey: ["champignons", "options"] });
    },
  });
};

export const useRestoreChampignon = () => {
  const queryClient = useQueryClient();
  
  return useMutation<ChampignonsResponse, Error, number>({
    mutationFn: (id) => services.champignon.restoreChampignon(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["champignons", "detail", id] });
      queryClient.invalidateQueries({ queryKey: ["champignons", "list"] });
      queryClient.invalidateQueries({ queryKey: ["champignons", "options"] });
    },
  });
};

export const useUploadChampignonImage = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ChampignonsResponse,
    Error,
    { id: number; imageFile: File; onProgress?: (percent: number) => void }
  >({
    mutationFn: ({ id, imageFile, onProgress }) =>
      services.champignon.uploadChampignonImage(id, imageFile, onProgress),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["champignons", "detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["champignons", "list"] });
    },
  });
};
